import React, { SetStateAction, useContext } from 'react';
import { useMemo } from 'react';
import {
  StyledDropDownMenu,
  StyledDropDownMenuItems,
  StyledDropDownMenuItem,
  StyledDropDownMenuItemContainder,
  Title,
  Item,
  PriceItem,
} from './AllWatches.styled';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { formatter } from '../../../utils/helpers/formatters';
import { setPriceRangeAction } from '../../../store/Product/action';
import { reducerTypes } from '../../../store/Product/types';
import LocalizedLink from '../../../components/Link/localized-link';
import { LanguageContext } from '../../../providers/LanguageProvider';

interface NavLinkDropDownMenuProps {
  allWatchesDropdownVisible: boolean;
  setAllWatchesDropdownVisible: (value: SetStateAction<boolean>) => void;
}

export const AllWatchesNavLinkDropDownMenu: React.FC<NavLinkDropDownMenuProps> = ({
  allWatchesDropdownVisible,
  setAllWatchesDropdownVisible,
}) => {
  const dispatch = useAppDispatch();
  const locale = useAppSelector((st) => st.locale);
  const selectedCurrency = useAppSelector((st) => st.currency.selectedCurrency);
  const { lang } = useContext(LanguageContext);

  const messages = useMemo(() => {
    return {
      categories: {
        title: locale['header_all_watches_categories_title'],
        items: [
          locale['header_all_watches_categories_item_0'],
          locale['header_all_watches_categories_item_1'],
          locale['header_all_watches_categories_item_2'],
          locale['header_all_watches_categories_item_3'],
          locale['header_all_watches_categories_item_4'],
        ],
        path: ['/all-watches', '/new-arrivals', '/brands', '/all-watches/mens', '/all-watches/womens'],
        state: [
          undefined,
          undefined,
          undefined,
          {
            state: {
              id: '',
              key: 'Gender',
              value: "Men's watch/Unisex",
            },
          },
          {
            state: {
              id: '',
              key: 'Gender',
              value: "Women's watch",
            },
          },
        ],
      },
      collections: {
        title: locale['header_all_watches_collections_title'],
        items: [
          locale['header_all_watches_collections_item_0'],
          locale['header_all_watches_collections_item_1'],
          locale['header_all_watches_collections_item_2'],
          locale['header_all_watches_collections_item_3'],
          locale['header_all_watches_collections_item_4'],
        ],
        path: [
          '/collections/Best_Sellers/cef8508f-5dff-43f7-150f-08dabceee634',
          `/all-watches?pageindex=0&Movement=Automatic&curr=${selectedCurrency}`,
          '/collections/Sport_Watches/0c1e3c4d-ca8d-4359-1510-08dabceee634',
          `/all-watches?pageindex=0&CaseMaterial=Steel&curr=${selectedCurrency}`,
          `/all-watches?pageindex=0&CaseMaterial=Platinum&curr=${selectedCurrency}`,
        ],
        state: [
          undefined,
          {
            state: {
              id: '',
              key: 'Movement',
              value: 'Automatic',
            },
          },
          undefined,
          {
            state: {
              id: '',
              key: 'CaseMaterial',
              value: 'Steel',
            },
          },
          {
            state: {
              id: '',
              key: 'CaseMaterial',
              value: 'Platinum',
            },
          },
        ],
      },
      prices: {
        title: locale['header_all_watches_prices_title'],
        items:
          lang === 'cz'
            ? [
                `${locale['header_all_watches_prices_item_0']} ${formatter(5000, selectedCurrency)}`,
                `Od ${formatter(5000, selectedCurrency)} do ${formatter(15000, selectedCurrency)}`,
                `Od ${formatter(15000, selectedCurrency)} do ${formatter(25000, selectedCurrency)}`,
                `Od ${formatter(25000, selectedCurrency)} do ${formatter(50000, selectedCurrency)}`,
                `Od ${formatter(50000, selectedCurrency)}+`,
              ]
            : [
                `${locale['header_all_watches_prices_item_0']} ${formatter(5000, selectedCurrency)}`,
                `${formatter(5000, selectedCurrency)} to ${formatter(15000, selectedCurrency)}`,
                `${formatter(15000, selectedCurrency)} to ${formatter(25000, selectedCurrency)}`,
                `${formatter(25000, selectedCurrency)} to ${formatter(50000, selectedCurrency)}`,
                `${formatter(50000, selectedCurrency)}+`,
              ],
        path: [
          `/all-watches?pageindex=0&pricemin=0&pricemax=5000&curr=${selectedCurrency}`,
          `/all-watches?pageindex=0&pricemin=5000&pricemax=15000&curr=${selectedCurrency}`,
          `/all-watches?pageindex=0&pricemin=15000&pricemax=25000&curr=${selectedCurrency}`,
          `/all-watches?pageindex=0&pricemin=25000&pricemax=50000&curr=${selectedCurrency}`,
          `/all-watches?pageindex=0&pricemin=50000&pricemax=999999&curr=${selectedCurrency}`,
        ],
        price: [
          [0, 5000],
          [5000, 15000],
          [15000, 25000],
          [25000, 50000],
          [50000, 999999],
        ],
      },
    };
  }, [locale, selectedCurrency]);

  if (!allWatchesDropdownVisible) return null;

  return (
    <StyledDropDownMenu>
      <StyledDropDownMenuItems>
        <StyledDropDownMenuItem>
          <StyledDropDownMenuItemContainder>
            <Title>{messages.categories.title}</Title>
            {messages.categories.items.map((item, index) => (
              <Item key={index}>
                <LocalizedLink
                  href={messages.categories.path[index]}
                  onClick={() => {
                    dispatch({ type: reducerTypes.GET_ALL_PRODUCTS, payload: [] });
                    dispatch({ type: reducerTypes.GET_FILTERS, payload: [] });
                    dispatch({ type: reducerTypes.IS_LOADING, payload: true });
                    setAllWatchesDropdownVisible(false);
                  }}
                >
                  {item}
                </LocalizedLink>
              </Item>
            ))}
          </StyledDropDownMenuItemContainder>
        </StyledDropDownMenuItem>

        <StyledDropDownMenuItem>
          <StyledDropDownMenuItemContainder>
            <Title>{messages.collections.title}</Title>
            {messages.collections.items.map((item, index) => (
              <Item key={index}>
                <LocalizedLink
                  href={messages.collections.path[index]}
                  onClick={() => {
                    dispatch({ type: reducerTypes.GET_ALL_PRODUCTS, payload: [] });
                    dispatch({ type: reducerTypes.GET_FILTERS, payload: [] });
                    dispatch({ type: reducerTypes.IS_LOADING, payload: true });
                    setAllWatchesDropdownVisible(false);
                  }}
                >
                  {item}
                </LocalizedLink>
              </Item>
            ))}
          </StyledDropDownMenuItemContainder>
        </StyledDropDownMenuItem>

        <StyledDropDownMenuItem>
          <StyledDropDownMenuItemContainder>
            <Title>{messages.prices.title}</Title>
            {messages.prices.items.map((item, index) => (
              <PriceItem key={index}>
                <LocalizedLink
                  href={messages.prices.path[index]}
                  onClick={() => {
                    dispatch({ type: reducerTypes.GET_ALL_PRODUCTS, payload: [] });
                    dispatch({ type: reducerTypes.GET_FILTERS, payload: [] });
                    dispatch({ type: reducerTypes.IS_LOADING, payload: true });
                    dispatch(setPriceRangeAction(messages.prices.price[index]));
                    setAllWatchesDropdownVisible(false);
                  }}
                >
                  {item}
                </LocalizedLink>
              </PriceItem>
            ))}
          </StyledDropDownMenuItemContainder>
        </StyledDropDownMenuItem>
      </StyledDropDownMenuItems>
    </StyledDropDownMenu>
  );
};
